import {Drawer, List, ListItem, Stack, TextField} from "@mui/material";
import React, {useCallback, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import type {BaseDates, BasePayers} from "../firebase/index.js";
import {
	  addExpenditure,
	  addItinerary,
	  addTrip,
	  areDateRangesEqual,
	  ExpenditureModel,
	  generateMainFirebaseRtdbKey,
	  generatePayerKeyFromPayerName,
	  generateTripDates,
	  ItineraryModel,
	  moveAllPlannedPlacesToUnassigned,
	  PayerModel,
	  TripModel
} from "../firebase/index.js";
import type {PayerDatum} from "../redux/AddNewTripPayerReducer.js";
import {payerSliceName, resetAllPayers} from "../redux/AddNewTripPayerReducer.js";
import APXActionButtons from "../reusables/APXActionButtons.jsx";
import APXDateRangePicker, {DateRange} from "../reusables/APXDateRangePicker.jsx";
import APXNumberSelector from "../reusables/APXNumberSelector.jsx";
import type {BSLProps} from "../reusables/js/APXBottomSheetLayoutUtils.js";
import AddNewPayerRow from "./expenditure/AddNewPayerRow.jsx";


export default function AddNewTripBSL({uid, onClose, trip}: BSLProps & { trip: TripModel }) {
	  const [where, setWhere] = useState("");
	  const [howMany, setHowMany] = useState(1);
	  const [dateRange, setDateRange] = useState(new DateRange());
	  const [note, setNote] = useState("");
	  
	  const [isInEditMode, setIsInEditMode] = useState(false);
	  const [forEditTripKey, setForEditTripKey] = useState("");
	  const [forEditTripDateRange, setForEditTripDateRange] = useState(new DateRange());
	  useEffect(() => {
			 const t: TripModel = trip;
			 if (/*TRUTHY->*/ !!t) {
					setIsInEditMode(true);
					setForEditTripKey(t.tripKey);
					setWhere(t.tripTo);
					setNote(t.tripNote);
					const baseDates: BaseDates = t.tripDates;
					if (/*TRUTHY->*/ !!baseDates) {
						  const entries = Object.entries(baseDates);
						  if (entries.length > 0) {
								 const startDate = new Date(Number(entries[0][0])).toISOString().split("T")[0];
								 const endDate = new Date(Number(entries[entries.length - 1][0])).toISOString().split("T")[0];
								 setDateRange(new DateRange(startDate, endDate));
								 setForEditTripDateRange(new DateRange(startDate, endDate));
						  }
					}
			 }
	  }, [trip]);
	  
	  const dispatch = useDispatch();
	  
	  useEffect(() => {
			 dispatch(resetAllPayers(howMany));
	  }, [howMany]);
	  
	  const payerData: PayerDatum[] = useSelector((state) => state[payerSliceName]);
	  
	  const handleCancel = () => {
			 onClose();
	  };
	  
	  const handleAdd = useCallback(() => {
			 if (/*FALSY->*/ !!!uid) {
					alert("Please login to add a trip");
					return;
			 }
			 
			 if (where.trim() === "") {
					alert("Trip destination cannot be empty");
					return;
			 }
			 
			 if (/*NOT->*/ !isInEditMode && payerData.some((payerDatum) => payerDatum.name.trim() === "")) {
					alert("Please fill at least one payer's details");
					return;
			 }
			 
			 const key = forEditTripKey || generateMainFirebaseRtdbKey();
			 
			 const trip = new TripModel(key, Date.now(), where.trim(), note.trim(), generateTripDates(dateRange));
			 
			 addTrip(uid, trip.tripKey, trip);
			 
			 areDateRangesEqual(dateRange, forEditTripDateRange) || moveAllPlannedPlacesToUnassigned(uid, trip.itineraryKey);
			 
			 
			 if (/*NOT->*/ !isInEditMode) {
					
					const itinerary = new ItineraryModel(key, {}, {});
					
					addItinerary(uid, trip.itineraryKey, itinerary);
					
					
					const payers: BasePayers = {};
					
					payerData.forEach((payerDatum) => {
						  const payerKey = generatePayerKeyFromPayerName(payerDatum.name);
						  payers[payerKey] = new PayerModel(payerKey, payerDatum.name.trim(), payerDatum.payingFor);
					});
					const expenditure = new ExpenditureModel(key, payers, {});
					
					addExpenditure(uid, trip.expenditureKey, expenditure);
					
			 }
			 
			 dispatch(resetAllPayers(0));
			 
			 handleCancel();
			 
	  }, /*NOT->*/ !isInEditMode ? [payerData] : [where, dateRange, note]);
	  
	  return (
				 <Drawer anchor="bottom" open={true} variant="temporary">
						<Stack spacing={2} sx={{p: 2}}>
							  <Stack direction="row" spacing={2} justifyContent={"space-between"} alignItems={"center"}>
									 <TextField required sx={{width: "200%"}} variant="outlined" label={"Where are you going?"} value={where}
													onChange={(event) => setWhere(event.target.value)}/>
									 {
											/*NOT->*/ !isInEditMode &&
												<APXNumberSelector required title={"How many are bearing the cost?"} value={howMany} onChange={(event) => setHowMany(Number(event.target.value))}/>
									 }
							  </Stack>
							  <APXDateRangePicker required={false} onDateRangeChange={(dateRange) => setDateRange(dateRange)} clear={false} maxToday={false}/>
							  {
									 /*NOT->*/ !isInEditMode &&
										 <Stack direction="row" spacing={2} justifyContent={"space-between"} alignItems={"center"}>
												<List sx={{width: "100%"}}>
													  {[...Array(howMany)].map((_, index) => {
															 return <ListItem key={index} sx={{p: 0, width: "100%", py: 1}}>
																	<AddNewPayerRow index={index}/>
															 </ListItem>;
													  })}
												</List>
										 </Stack>
							  }
							  <TextField fullWidth variant="outlined" label={"Note"} value={note} onChange={(event) => setNote(event.target.value)}/>
							  <APXActionButtons onAdd={handleAdd} onCancel={handleCancel}/>
						</Stack>
				 </Drawer>
	  );
}